import { ReactNode, useCallback, useMemo, useState } from "react";
import { isAccountTypeEligibleForRole } from "@common/constants/domains/accounting/accountRole";
import { AccountRole } from "@common/types/domains/accounting/account";
import { EntityProfileType } from "@common/types/entity";
import { ChartOfAccountsTemplateAccountsData } from "@api/coaTemplate.api";
import { Button } from "@design-system/Button/Button";
import { RequiredAccountsMappingDrawer } from "../../roles/RequiredAccountsMappingDrawer";
import {
    appendCreatedRoleAccount,
    assignRole,
    canTakeRole,
    CreateRoleAccountValues,
    dropUnassignedCreatedAccounts,
    isCreatedRoleAccountId,
    RoleMappingAccount,
} from "../../roles/requiredAccountsMapping";
import { DraftAccount } from "../draftState";
import { NestedEditorRow } from "../nested/nestedRowTypes";
import { TemplateReassignRoleModal } from "./TemplateReassignRoleModal";

interface Params {
    accounts: readonly DraftAccount[];
    savedAccounts: ChartOfAccountsTemplateAccountsData["accounts"];
    entityLegalType: EntityProfileType;
    applyRoleAssignments: (accounts: readonly RoleMappingAccount[]) => void;
}

interface TemplateRoleTools {
    onReassignRole: (row: NestedEditorRow) => void;
    isManageOpen: boolean;
    toolbarButton: ReactNode;
    overlays: ReactNode;
}

const CONFIRM_LABEL = "Done";

const addCreatedAccount = ({
    accounts,
    values,
}: {
    accounts: readonly RoleMappingAccount[];
    values: CreateRoleAccountValues;
}): RoleMappingAccount[] =>
    appendCreatedRoleAccount({
        accounts,
        role: values.role,
        buildAccount: (id) => ({
            id,
            name: values.name,
            codeText: values.codeText,
            type: values.type,
            role: null,
        }),
    });

export const useTemplateRoleTools = ({
    accounts,
    savedAccounts,
    entityLegalType,
    applyRoleAssignments,
}: Params): TemplateRoleTools => {
    const [reassignRole, setReassignRole] = useState<AccountRole | null>(null);
    const [isReassignOpen, setIsReassignOpen] = useState(false);
    const [workingAccounts, setWorkingAccounts] = useState<
        RoleMappingAccount[] | null
    >(null);

    const roleAccounts = useMemo(() => {
        const savedById = new Map(
            savedAccounts.map((account) => [account.id, account]),
        );
        return accounts
            .filter(
                (account) =>
                    account.role != null ||
                    canTakeRole({ account, saved: savedById.get(account.id) }),
            )
            .map(({ id, name, codeText, type, role }) => ({
                id,
                name,
                codeText,
                type,
                role: role ?? null,
            }));
    }, [accounts, savedAccounts]);

    const createdAccounts = useMemo(
        () =>
            (workingAccounts ?? []).filter(({ id }) =>
                isCreatedRoleAccountId(id),
            ),
        [workingAccounts],
    );

    const codeContextAccounts = useMemo(() => {
        const variableAccountIds = new Set(
            savedAccounts
                .filter(({ variableKind }) => variableKind != null)
                .map(({ id }) => id),
        );
        return [
            ...accounts.filter(({ id }) => !variableAccountIds.has(id)),
            ...createdAccounts,
        ];
    }, [accounts, savedAccounts, createdAccounts]);

    const nameContextAccounts = useMemo(
        () => [...accounts, ...createdAccounts],
        [accounts, createdAccounts],
    );

    const targetAccounts = useMemo(
        () =>
            reassignRole === null
                ? []
                : roleAccounts.filter(
                      (account) =>
                          account.role === null &&
                          isAccountTypeEligibleForRole(
                              reassignRole,
                              account.type,
                          ),
                  ),
        [roleAccounts, reassignRole],
    );

    const handleOpenReassign = useCallback((row: NestedEditorRow) => {
        const role = row.account?.role ?? null;
        if (role !== null) {
            setReassignRole(role);
            setIsReassignOpen(true);
        }
    }, []);

    const handleReassign = useCallback(
        (targetAccountId: string) => {
            if (reassignRole === null) {
                return;
            }
            applyRoleAssignments(
                assignRole({
                    accounts: roleAccounts,
                    role: reassignRole,
                    accountId: targetAccountId,
                }),
            );
            setIsReassignOpen(false);
        },
        [applyRoleAssignments, reassignRole, roleAccounts],
    );

    const handleConfirmManage = () => {
        if (workingAccounts !== null) {
            applyRoleAssignments(
                dropUnassignedCreatedAccounts(workingAccounts),
            );
        }
        setWorkingAccounts(null);
    };

    return {
        onReassignRole: handleOpenReassign,
        isManageOpen: workingAccounts !== null,
        toolbarButton: (
            <Button
                variant="primary"
                size="md"
                onClick={() => setWorkingAccounts(roleAccounts)}
            >
                Manage roles
            </Button>
        ),
        overlays: (
            <>
                {reassignRole !== null && (
                    <TemplateReassignRoleModal
                        show={isReassignOpen}
                        role={reassignRole}
                        targetAccounts={targetAccounts}
                        onReassign={handleReassign}
                        onHide={() => setIsReassignOpen(false)}
                        onExited={() => setReassignRole(null)}
                    />
                )}
                {workingAccounts !== null && (
                    <RequiredAccountsMappingDrawer
                        open
                        accounts={workingAccounts}
                        codeContextAccounts={codeContextAccounts}
                        nameContextAccounts={nameContextAccounts}
                        entityLegalType={entityLegalType}
                        confirmLabel={CONFIRM_LABEL}
                        isConfirming={false}
                        isDiscardPromptOpen={false}
                        onAssign={({ role, accountId }) =>
                            setWorkingAccounts((current) =>
                                assignRole({
                                    accounts: current ?? [],
                                    role,
                                    accountId,
                                }),
                            )
                        }
                        onCreateAccount={(values) =>
                            setWorkingAccounts((current) =>
                                addCreatedAccount({
                                    accounts: current ?? [],
                                    values,
                                }),
                            )
                        }
                        onClose={() => setWorkingAccounts(null)}
                        onConfirm={handleConfirmManage}
                    />
                )}
            </>
        ),
    };
};
